import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "./QualityManagementSystem.module.scss";
import { useDispatch, useSelector } from "react-redux";
import Separator from "../../components/Separator/Separator";
import axiosInstance from "../../utils/utils";
import {
  setConfluencePage,
  setConfluenceTree,
} from "../../redux/confluenceStore";
import classNames from "classnames";
import { setLoadingContext } from "../../redux/loaderStore";
import Loader from "../../components/Loader/Loader";
import _ from "lodash";
import pageNotFoundIllustration from "../../icons/404.svg";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { hasRealContent } from "../../utils/helpers";
import { COMPONENT_LOADER, MODAL_LOADER } from "../../constants/loader";
import ConfluencePageDataModal from "../../modals/ConfluencePageDataModal";
import { showModal } from "../../redux/modalStore";
import { setModalConfluencePage } from "../../redux/modalConfluenceStore";
import CustomTable from "../../components/CustomTable/CustomTable";
import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs";
import Button from "../../components/Button/Button";
import ExpandableMenu from "../../components/ExpandableMenu/ExpandableMenu";
import { ReactComponent as ArrowUpIcon } from "../../icons/circle-arrow-up.svg";
import Input from "../../components/Input/Input";
import MainLayout from "../../layouts/MainLayout/MainLayout";
import useFetchOrganizationData from "../../actions/useFetchOrganizationData";
import { updateFloatingBarVisible } from "../../redux/layoutStore";

const QualityManagementSystem = ({ getConfluenceTree }) => {
  const { orgId, pageId } = useParams();
  const location = useLocation();
  const leftSideRef = useRef(null);
  const rightSideRef = useRef(null);

  const hasFetchedPageData = useRef(false); // Ref to control the first fetch

  const fetchingTree = useRef(false);
  const fetchingPage = useRef(false);

  const navigate = useNavigate();

  const [selectedChapter, setSelectedChapter] = useState(null);
  const [selectedPageId, setSelectedPageId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const confluenceTreeData = useSelector(
    (state) => state.confluence.confluenceTreeData,
  );
  const confluencePageData = useSelector(
    (state) => state.confluence.confluencePageData,
  );

  const modalConfluencePageData = useSelector(
    (state) => state.modalConfluence.modalConfluencePageData,
  );
  const module = useSelector((state) => state.user.modules[orgId]);
  const loadingContexts = useSelector((state) => state.loader.loadingContexts);
  const notFoundPage = useSelector((state) => state.layout.notFoundPage);
  const currentOrganization = useSelector(
    (state) => state.user.organizations[orgId],
  );

  const isComponentLoading = loadingContexts[COMPONENT_LOADER];

  // Fetch organization data when navigating through URL
  useFetchOrganizationData();

  useEffect(() => {
    dispatch(updateFloatingBarVisible(true));
  }, []);

  const clearConfluenceData = () => {
    dispatch(setConfluenceTree(null)); // Clear the confluence tree data
    dispatch(setConfluencePage(null)); // Clear the confluence page data
  };

  const dispatch = useDispatch();

  const sessionToken = localStorage.getItem("session_token");

  // Extract the module path from the URL
  const pathSegments = location.pathname.split("/");
  const modifiedPath = `/${pathSegments[2]}`; // The third segment is the module path

  const extractChapter = (pageName) => {
    const match = pageName.match(/^([A-H]\d*)\s*[-–]\s*/);
    return match ? match[1] : null;
  };

  // Find the module object corresponding to the current path
  const currentModule = module?.find((mod) => mod.path === modifiedPath);

  const getConfluencePageData = useCallback(
    async (pageId, pageName) => {
      // Prevent duplicate calls for page data
      if (fetchingPage.current) return;
      fetchingPage.current = true;

      setSelectedPageId(pageId); // Update state immediately
      const chapter = extractChapter(pageName);
      setSelectedChapter(chapter);

      if (!sessionToken) {
        clearConfluenceData();
        fetchingPage.current = false; // Reset fetching flag
        return;
      }

      // Extract the space parameter from the module object
      const space = currentModule?.params?.space || "";

      const options = {
        method: "GET",
        url: `/confluence/page?org_id=${orgId}&space_key=${space}&page_id=${pageId}`,
        headers: {
          "session-token": sessionToken,
        },
      };

      dispatch(
        setLoadingContext({ context: COMPONENT_LOADER, isLoading: true }),
      );

      try {
        const response = await axiosInstance.request(options);
        dispatch(setConfluencePage(response.data));
      } catch (error) {
        console.error("Error fetching page:", error);
        clearConfluenceData();
        dispatch(
          showModal({
            name: "error_modal",
          }),
        );
      } finally {
        dispatch(
          setLoadingContext({ context: COMPONENT_LOADER, isLoading: false }),
        );
        fetchingPage.current = false; // Reset fetching flag
      }
    },
    [currentModule, orgId, sessionToken, dispatch],
  );

  useEffect(() => {
    const fetchData = async () => {
      if (!sessionToken) return;

      try {
        // Step 1: Fetch tree data if not already fetched
        if (!fetchingTree.current && !confluenceTreeData) {
          fetchingTree.current = true; // Mark as fetching
          await getConfluenceTree(`/${orgId}/qms`, sessionToken);
          fetchingTree.current = false; // Reset after fetch
        }

        // Step 2: Fetch page data only after tree data is loaded
        if (confluenceTreeData && pageId && !fetchingPage.current) {
          fetchingPage.current = true; // Mark as fetching
          const response = await axiosInstance.get(
            `/confluence/page?org_id=${orgId}&page_id=${pageId}`,
            { headers: { "session-token": sessionToken } },
          );
          dispatch(setConfluencePage(response.data));
          fetchingPage.current = false; // Reset after fetch
        }
      } catch (error) {
        console.error("Error loading data:", error);
        clearConfluenceData();
        fetchingTree.current = false;
        fetchingPage.current = false;
      }
    };

    fetchData();

    // Reset fetching states when orgId changes
    return () => {
      fetchingTree.current = false;
      fetchingPage.current = false;
    };
  }, [
    orgId,
    pageId,
    sessionToken,
    confluenceTreeData,
    getConfluenceTree,
    dispatch,
  ]);

  const getModalConfluencePageData = (space, pageId) => {
    const options = {
      method: "GET",
      url: `/confluence/page?org_id=${orgId}&space_key=${space}&page_id=${pageId}`,
      headers: {
        "session-token": sessionToken,
      },
    };

    dispatch(setLoadingContext({ context: MODAL_LOADER, isLoading: true }));

    return axiosInstance
      .request(options)
      .then((response) => {
        dispatch(
          setLoadingContext({ context: MODAL_LOADER, isLoading: false }),
        );
        dispatch(setModalConfluencePage(response.data));
      })
      .catch(() => {
        dispatch(
          setLoadingContext({ context: MODAL_LOADER, isLoading: false }),
        );
        dispatch(
          showModal({
            name: "error_modal",
          }),
        );
      });
  };

  // Function to generate JSX for the page names
  const renderPageNames = (data, level = 0, currentPath) => {
    const isActive = confluencePageData?.title === data.page_name;

    return (
      <div
        key={data.page_id}
        className={styles.content}
        style={{ marginLeft: level * 5 }}
      >
        <div
          onClick={() =>
            getConfluencePageData(
              data.page_id,
              data.page_name,
              location.pathname,
            )
          }
          className={classNames(styles.pageItems, isActive && styles.active)}
        >
          {data.page_name}
        </div>
        {data?.children?.length > 0 && (
          <div className={styles.items}>
            {data?.children?.map((child) => (
              <div key={child.page_id}>{renderPageNames(child, level + 1)}</div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const isConfluencePageData = !_.isEmpty(confluencePageData);

  // Send request for the first page item when the component mounts and confluenceTreeData is available
  useEffect(() => {
    if (!orgId || !sessionToken || !confluenceTreeData) return;

    if (
      confluenceTreeData?.children?.length > 0 &&
      !hasFetchedPageData.current
    ) {
      const firstPageId = confluenceTreeData.children[0].page_id;
      hasFetchedPageData.current = true;
      getConfluencePageData(firstPageId, modifiedPath);
    }

    return () => {
      clearConfluenceData();
      hasFetchedPageData.current = false; // Reset when orgId or tree changes
    };
  }, [orgId, sessionToken, confluenceTreeData]);

  useEffect(() => {
    if (confluencePageData?.title) {
      const chapter = extractChapter(confluencePageData.title);
      setSelectedChapter(chapter || null);
    }
  }, [confluencePageData]);

  const isContentEmpty = !hasRealContent(confluencePageData?.body);

  const addOnClickHandlersToLinks = (htmlContent) => {
    const parser = new DOMParser();
    const htmlDoc = parser.parseFromString(htmlContent, "text/html");

    const links = htmlDoc.querySelectorAll("a");
    const fileExtensions = /\.(pdf|docx|xlsx|csv|pptx|txt|zip)$/i; // Files extensions

    links.forEach((link) => {
      const cSpace = link.getAttribute("c-space");
      const cPageId = link.getAttribute("c-page_id");
      const href = link.getAttribute("href");

      if (href && fileExtensions.test(href)) {
        // Handle file links: open in a new tab
        link.setAttribute("target", "_blank");
        link.setAttribute("rel", "noopener noreferrer");
      } else if (cSpace && cPageId) {
        // Handle links with `c-space` and `c-page_id` (internal links for modal)
        link.setAttribute(
          "onclick",
          `handleLinkClick(event, '${cSpace}', ${cPageId})`,
        );
      } else if (href && href.startsWith("http")) {
        // Handle other external links
        link.setAttribute("target", "_blank");
        link.setAttribute("rel", "noopener noreferrer");
      }
    });

    return htmlDoc.body.innerHTML;
  };

  const scrollToTop = () => {
    if (rightSideRef.current) {
      rightSideRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Function to handle link clicks
  const handleLinkClick = (event, cSpace, cPageId) => {
    event.preventDefault();
    getModalConfluencePageData(cSpace, cPageId);
    dispatch(
      showModal({
        name: "quality_management_system_modal",
      }),
    );
  };

  useEffect(() => {
    window.handleLinkClick = handleLinkClick;
  }, []);

  // Handler for opening the "Edit QMS" link in a new tab
  const handleEditQMSClick = () => {
    const space = currentModule?.params?.space || "";
    const pageId = selectedPageId;
    const atlassianDomain = currentOrganization?.AtlassianDomain;

    if (atlassianDomain && space && pageId) {
      const editUrl = `https://${atlassianDomain}/wiki/spaces/${space}/pages/${pageId}`;
      window.open(editUrl, "_blank");
    } else {
      console.error("Could not construct the Atlassian edit URL.");
    }
  };

  const filterTreeData = (treeData, query) => {
    if (!query) return treeData; // Return the original tree data if there's no query

    const result = [];

    const searchTree = (nodes) => {
      nodes.forEach((node) => {
        if (node.page_name.toLowerCase().includes(query.toLowerCase())) {
          result.push(node);
        }
        if (node.children?.length > 0) {
          searchTree(node.children);
        }
      });
    };

    searchTree(treeData);
    return result; // Return only the matching nodes
  };

  const filteredTreeData = filterTreeData(
    confluenceTreeData?.children || [],
    searchQuery,
  );

  return (
    <MainLayout>
      <div className={styles.column}>
        <div className={styles.top}>
          <Breadcrumbs data={currentModule} />
          {confluenceTreeData?.page_name === "Quality Management System" && (
            <Button
              text="Edit QMS"
              className={styles.editButton}
              onClick={handleEditQMSClick}
            />
          )}
        </div>
        {notFoundPage ? (
          <div className={styles.pageNotFoundWrapper}>
            <img src={pageNotFoundIllustration} alt="404 Illustration" />
            <span>
              Oops! We can't seem to find the page you're looking for.
            </span>
          </div>
        ) : (
          <>
            <Separator />
            <div
              className={classNames(
                styles.wrapper,
                isConfluencePageData && styles.rightSideActive,
              )}
            >
              <div className={styles.leftSide}>
                <Input
                  placeholder="Search pages..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  isClearable
                  onClear={() => setSearchQuery("")}
                />
                {searchQuery ? (
                  filteredTreeData?.length > 0 ? (
                    <div className={styles.results}>
                      {filteredTreeData.map((page) => (
                        <div
                          key={page.page_id}
                          onClick={() =>
                            getConfluencePageData(page.page_id, page.page_name)
                          }
                          className={classNames(styles.pageItems, {
                            [styles.active]: selectedPageId === page.page_id,
                          })}
                        >
                          {page.page_name}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.noResults}>No pages found</div>
                  )
                ) : (
                  <ExpandableMenu
                    treeData={confluenceTreeData?.children || []}
                    onNodeClick={(pageId, pageName) => {
                      navigate(`/${orgId}/qms/${pageId}`); // Update the URL
                      getConfluencePageData(pageId, pageName); // Fetch the page data
                    }}
                    selectedPageId={selectedPageId}
                  />
                )}
              </div>

              <Separator vertical className={styles.separator} />
              {(isComponentLoading || isConfluencePageData) && (
                <div className={styles.rightSide} ref={rightSideRef}>
                  {isComponentLoading && (
                    <Loader text="Loading, thank you for your patience." />
                  )}
                  {isConfluencePageData && !isComponentLoading && (
                    <div className={styles.rightSideWrapper}>
                      <h1>{confluencePageData?.title}</h1>
                      <CustomTable
                        selectedChapter={selectedChapter}
                        className={classNames(
                          isContentEmpty && styles.fullHeight,
                        )}
                      />
                      {!isContentEmpty && (
                        <>
                          <Separator className={styles.dataSeparator} />
                          <div className={classNames(styles.dataWrapper)}>
                            <div
                              id="confluence-content"
                              dangerouslySetInnerHTML={{
                                __html: addOnClickHandlersToLinks(
                                  confluencePageData?.body,
                                ),
                              }}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
        <ConfluencePageDataModal
          modalConfluencePageData={modalConfluencePageData}
          addOnClickHandlersToLinks={addOnClickHandlersToLinks}
        />
      </div>
      {confluencePageData && (
        <div className={styles.backToTop} onClick={() => scrollToTop()}>
          <ArrowUpIcon />
        </div>
      )}
    </MainLayout>
  );
};

export default QualityManagementSystem;
